import React, { useMemo, useEffect, useCallback } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';

import { JobApplicationStatusKey } from '@restworld/utility-types';
import { useAuth } from '@restworld/feature-authentication';
import { useAnalytics, WorkerEntryPointProps } from '@restworld/utils-common';
import NextLink from 'next/link';
import { Text, Button, ButtonProps, Popover, PopoverTrigger } from '@restworld/ui-ds-base';

import JobBoardPixels from './worker-entry-point.components/job-board-pixels';
import {
  UploadCVUI,
  EntryPointChecklistProvider,
  useEntryPointCheckListContext,
  UploadedCVUI,
} from './worker-entry-point.components';
import {
  hasUserAppliedSpontaneousApplicationAtom,
  useShowSpontaneousApplication,
} from './use-show-spontaneous-application';
import { useJobExpireInfo, useJobPositionId } from '../../hooks';
import {
  isCVUploadProcessCompletedAtom,
  jobApplicationModalAlreadyDisplayedAtom,
  showConfirmModalAtom,
  spontaneousApplyDataAtom,
  workerIdAtom,
} from './worker-entry-point.atoms';
import { entryPointModalVisibleAtom } from './worker-entry-point-modal';
import { resumeTargetFor } from './apply-intent-guard';
import { useRouter } from 'next/router';
import {
  isJobApplicationDiscardedAtom,
  jobApplicationAtom,
  jobPositionApiDataAtom,
  jobPositionApplyStatusAtom,
} from '../job-position/job-position.atoms';
import {
  phoneNumberVerificationModalVisibleAtom,
  useShouldShowPhoneNumberVerificationModal,
} from '../common-phone-number-otp-validation-modal/update-phone-number-modal';

export const WorkerEntryPointButton = ({
  children,
  fullwidth,
  applicationType = 'job-apply',
  restaurantID,
  label,
  testId,
  tooltipTitle,
}: WorkerEntryPointProps) => {
  const jobPositionId = useJobPositionId();
  const [showEntryPoint, toggleEntryPoint] = useAtom(entryPointModalVisibleAtom);
  const openEntryPoint = useCallback(() => toggleEntryPoint(true), [toggleEntryPoint]);
  const setWorkerId = useSetAtom(workerIdAtom);

  const [isCVUploadProcessCompleted] = useAtom(isCVUploadProcessCompletedAtom);
  const [, confirmModalToggler] = useAtom(showConfirmModalAtom);
  const [, setSpontaneousApplyData] = useAtom(spontaneousApplyDataAtom);
  const setJobApplicationModalAlreadyDisplayed = useSetAtom(jobApplicationModalAlreadyDisplayedAtom);
  const didUserSpontaneousApply = useAtomValue(hasUserAppliedSpontaneousApplicationAtom);
  const jobPositionApiData = useAtomValue(jobPositionApiDataAtom);
  const [jobPositionApplyStatus] = useAtom(jobPositionApplyStatusAtom);
  const jobApplication = useAtomValue(jobApplicationAtom);

  const { trackPHEvent, fbTrackWorkerStartsApplication } = useAnalytics();

  const restaurantId = jobPositionApiData?.restaurant?.id;
  const jopPositionId = jobPositionApiData?.id;
  const jobStatusKey = jobApplication?.status?.key as JobApplicationStatusKey;
  const isProposedJobApplication = restaurantId && jopPositionId && jobStatusKey === 'worker_jp_proposed';
  const { user, isAuthenticated } = useAuth();
  const { shouldShowPhoneNumberVerificationModal } = useShouldShowPhoneNumberVerificationModal();

  const onTrackApplyJobEvent = useCallback(() => {
    trackPHEvent('worker_initiate_apply_job', {
      job_position_id: jobPositionApiData?.id,
      job_title: jobPositionApiData?.job_title,
      restaurant_name: jobPositionApiData?.restaurant?.restaurant_name,
      category: jobPositionApiData?.occupation?.label_it?.label,
      logged_in: isAuthenticated,
    });
    const isProd = process.env.NEXT_PUBLIC_VERCEL_ENV === 'production';
    if (jobPositionApiData?.hirematic_flag && isProd) {
      window?.ppln('track', 'conversionRegistered', { step: 'start' });
    }
    fbTrackWorkerStartsApplication();
  }, [
    isAuthenticated,
    jobPositionApiData?.id,
    jobPositionApiData?.job_title,
    jobPositionApiData?.occupation?.label_it?.label,
    jobPositionApiData?.restaurant?.restaurant_name,
    jobPositionApiData?.hirematic_flag,
    fbTrackWorkerStartsApplication,
    trackPHEvent,
  ]);
  const router = useRouter();
  const { t } = useTranslation();
  const isJobApplicationDiscarded = useAtomValue(isJobApplicationDiscardedAtom);
  const setPhoneNumberVerificationModalVisible = useSetAtom(phoneNumberVerificationModalVisibleAtom);

  const { isJpInActive, isLoading: jobApplicationStatusLoading } = useJobExpireInfo();
  const btnTitle = useMemo(
    () =>
      jobPositionApplyStatus
        ? jobPositionApiData?.ats_applyurl
          ? t('common:continue_the_application_on_ats')
          : t('onboarding-page:monitor_the_progress')
        : t('common:apply_now'),
    [jobPositionApplyStatus, t, jobPositionApiData?.ats_applyurl]
  );
  const handleClick = useCallback(async () => {
    if (user?.type === 'worker' && !jobPositionApplyStatus && shouldShowPhoneNumberVerificationModal) {
      setPhoneNumberVerificationModalVisible(true);
      return;
    }
    if (jobPositionApiData?.ats_applyurl && jobPositionApplyStatus) {
      window.open(jobPositionApiData.ats_applyurl, '_blank');
      return;
    }
    if ((didUserSpontaneousApply && applicationType === 'spontaneous-apply') || jobPositionApplyStatus) {
      router?.push(`/candidature/${jobPositionId}`);
      return;
    }
    if (user?.type !== 'lead' && applicationType === 'job-apply' && isAuthenticated && !jobPositionApplyStatus) {
      confirmModalToggler(true);
    }
    // The entry point applies for the button that opened it: a spontaneous one for its venue, a
    // job one for the job of the page. A job button clears what a venue page left behind: the
    // atom survives client-side navigation, so venue X → an open job of X in the same tab used to
    // send that job's application to X as a spontaneous one (RW-418).
    setSpontaneousApplyData(
      applicationType === 'spontaneous-apply'
        ? {
            isSpontaneousEntry: true,
            applicationType,
            restaurantID,
            didUserSpontaneousApply,
            showCoverLetter: isAuthenticated,
          }
        : {}
    );
    openEntryPoint();
    onTrackApplyJobEvent();
  }, [
    applicationType,
    confirmModalToggler,
    didUserSpontaneousApply,
    isAuthenticated,
    jobPositionApplyStatus,
    jobPositionId,
    onTrackApplyJobEvent,
    openEntryPoint,
    restaurantID,
    router,
    setPhoneNumberVerificationModalVisible,
    setSpontaneousApplyData,
    shouldShowPhoneNumberVerificationModal,
    user?.type,
    jobPositionApiData?.ats_applyurl,
  ]);
  const buttonProps = useMemo(
    () =>
      // No `key` here: this object is spread into <Button> (not a list item), and a `key` in a
      // spread triggers React's "key prop spread" warning.
      ({
        onClick: handleClick,
        fullwidth,
        disabled:
          user?.type === 'ambassador' ||
          user?.type === 'employer' ||
          user?.type === 'employer_contact' ||
          isProposedJobApplication,
        testId: `${testId}-cta-btn`,
      } as ButtonProps),
    [fullwidth, handleClick, isProposedJobApplication, testId, user?.type]
  );

  const { showSpontaneousApplicationLoading } = useShowSpontaneousApplication({
    restaurantID,
    isSpontaneousEntry: applicationType === 'spontaneous-apply',
  });

  // Written without a click in one place only: the venue page, whose return from login
  // (`?apply=true`, see `apply-intent-guard.ts`) opens the entry point with no click, possibly in
  // a fresh tab. Anywhere else the click above writes it, and this only keeps it current (signed
  // in, applied) while it is still this venue's.
  const isVenuePage = resumeTargetFor({ pathname: router?.pathname ?? '' }).surface === 'venue';
  useEffect(() => {
    if (applicationType !== 'spontaneous-apply') return;
    setSpontaneousApplyData((current) =>
      isVenuePage || (current?.isSpontaneousEntry && current.restaurantID === restaurantID)
        ? {
            isSpontaneousEntry: true,
            applicationType,
            restaurantID,
            didUserSpontaneousApply,
            showCoverLetter: isAuthenticated,
          }
        : current
    );
  }, [applicationType, didUserSpontaneousApply, isAuthenticated, isVenuePage, restaurantID, setSpontaneousApplyData]);

  useEffect(() => {
    setJobApplicationModalAlreadyDisplayed(showEntryPoint);
  }, [setJobApplicationModalAlreadyDisplayed, showEntryPoint]);

  useEffect(() => {
    if (user?.id && isAuthenticated) setWorkerId(user?.id);
  }, [isAuthenticated, setWorkerId, user?.id]);

  const renderButtonChildren = useMemo(() => {
    if (children)
      return tooltipTitle ? (
        <Popover
          trigger={PopoverTrigger.HOVER}
          panelClassName={'md:min-w-[16rem]'}
          haveDefaultButtonStyle={false}
          buttonClassName={
            'focus-visible:border-none focus-visible:ring-0 focus-visible:shadow-none focus-visible:outline-none'
          }
        >
          <Popover.Button>
            {React.Children.map(children, (child) =>
              React.cloneElement(child, {
                onClick: handleClick,
              })
            )}
          </Popover.Button>
          <Popover.Panel>
            <div className="text-sm">{tooltipTitle}</div>
          </Popover.Panel>
        </Popover>
      ) : (
        React.Children.map(children, (child) =>
          React.cloneElement(child, {
            onClick: handleClick,
          })
        )
      );

    if (isJobApplicationDiscarded)
      return (
        <NextLink href={`/candidature/${jobPositionId}`} className="text-primary-800 cursor-pointer w-full">
          <Button className="text-purple-500" fullwidth>
            {t('job-position-page:discover_how_it_went')}
          </Button>
        </NextLink>
      );
    if (applicationType === 'spontaneous-apply')
      return (
        <Button {...buttonProps} variant="primary" loading={showSpontaneousApplicationLoading}>
          {didUserSpontaneousApply ? t('common:show_application') : label || t('common:spontaneous_application')}
        </Button>
      );

    if (user?.type === 'ambassador' || user?.type === 'employer' || user?.type === 'employer_contact')
      return (
        <Popover
          trigger={PopoverTrigger.HOVER}
          panelClassName={'md:min-w-[16rem]'}
          haveDefaultButtonStyle={false}
          buttonClassName={
            'focus-visible:border-none focus-visible:ring-0 focus-visible:shadow-none focus-visible:outline-none'
          }
        >
          <Popover.Button>
            <Button {...{ ...buttonProps, as: 'a' }}>{btnTitle}</Button>
          </Popover.Button>
          <Popover.Panel>
            <div className="text-sm">
              {`${t('onboarding-page:to_apply_you_must_be_authenticated_as_a_worker_and_not_as')} ${
                user?.type === 'ambassador' ? 'ambassador' : 'employer'
              }`}
              <span role="img" aria-label="cry-face">
                {' '}
                😢
              </span>
            </div>
          </Popover.Panel>
        </Popover>
      );

    if (jobPositionApplyStatus)
      return <Button {...{ ...buttonProps, loading: jobApplicationStatusLoading }}>{btnTitle}</Button>;

    if (!isJpInActive && !jobPositionApplyStatus)
      return (
        <Popover
          trigger={PopoverTrigger.CLICK}
          panelClassName={'min-w-[16rem]'}
          haveDefaultButtonStyle={false}
          fullWidth={true}
        >
          <Popover.Button>
            <Button disabled fullwidth>
              {btnTitle}
            </Button>
          </Popover.Button>
          <Popover.Panel>
            <Text color="muted" className="text-black">
              {t('onboarding-page:no_longer_accepting_applications_for_this_offer')}
              <span role="img" aria-label="cry">
                &#128557;
              </span>
            </Text>
          </Popover.Panel>
        </Popover>
      );

    return (
      <Button {...{ ...buttonProps, loading: jobApplicationStatusLoading }} testId={`${testId}-cta-btn`}>
        {btnTitle}
      </Button>
    );
  }, [
    children,
    isJobApplicationDiscarded,
    jobPositionId,
    applicationType,
    buttonProps,
    didUserSpontaneousApply,
    t,
    label,
    showSpontaneousApplicationLoading,
    user?.type,
    jobPositionApplyStatus,
    jobApplicationStatusLoading,
    isJpInActive,
    btnTitle,
    testId,
    handleClick,
  ]);

  return (
    <>
      {renderButtonChildren}
      {isCVUploadProcessCompleted ? <JobBoardPixels /> : null}
    </>
  );
};

export { UploadCVUI, EntryPointChecklistProvider, useEntryPointCheckListContext, UploadedCVUI };
